import { useForm } from 'react-hook-form';
import { useCallback, useState } from 'react';
import classNames from 'classnames';
import TextField from '@amf/shared/components/form/TextField';
import TextArea from '@amf/shared/components/form/TextArea';
import Button from '@amf/shared/components/button/Button';
import SelectField from '@amf/shared/components/form/SelectField';
import {
  validateDIC,
  validateEmail,
  validateICO,
  validatePhoneNumber,
  validateZipCode,
} from '@amf/shared/utils/string';

type DressSize = 'XS' | 'S' | 'M' | 'L' | 'XL' | '2XL' | '3XL' | '4XL';

export enum ReprePromoteType {
  BASIC = 'basic',
  PREMIUM = 'premium',
}

export type ReprePromoteData = {
  type: ReprePromoteType;

  // Required
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  zip: string;

  // NameTags & Dress
  heartNameTag: string;
  dressNameTag: string | null;
  dressNumber: number | null;
  dressSize: DressSize | null;

  // Billing address
  billingZip: string | null;
  billingCity: string | null;
  billingStreet: string | null;

  // Optional
  identificationNumber: string | null;
  vatNumber: string | null;
  companyName: string | null;
  note: string | null;
};

type Props = {
  onSubmit?: (data: ReprePromoteData) => Promise<void>;
  className?: string;
};

function ReprePromote({ onSubmit, className }: Props) {
  const [isLoading, setLoading] = useState(false);
  const form = useForm<ReprePromoteData>({
    defaultValues: {
      type: ReprePromoteType.BASIC,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      street: '',
      city: '',
      zip: '',

      heartNameTag: '',
      dressNameTag: null,
      dressNumber: null,
      dressSize: null,

      billingZip: null,
      billingCity: null,
      billingStreet: null,

      identificationNumber: null,
      vatNumber: null,
      companyName: null,
      note: null,
    },
  });

  const submit = useCallback(
    async (data: ReprePromoteData) => {
      if (!onSubmit) return;
      setLoading(true);
      await onSubmit({
        ...data,
        companyName: data.companyName && data.companyName.length === 0 ? null : data.companyName,
        identificationNumber:
          data.identificationNumber && data.identificationNumber.length === 0
            ? null
            : data.identificationNumber,
        note: data.note && data.note.length === 0 ? null : data.note,
        billingZip: data.billingZip && data.billingZip.length === 0 ? null : data.billingZip,
        billingStreet:
          data.billingStreet && data.billingStreet.length === 0 ? null : data.billingStreet,
        billingCity: data.billingCity && data.billingCity.length === 0 ? null : data.billingCity,
        vatNumber: data.vatNumber && data.vatNumber.length === 0 ? null : data.vatNumber,
      });
      setLoading(false);
    },
    [onSubmit],
  );

  const type = form.watch('type');
  return (
    <form
      className={classNames('ReprePromote__form', className)}
      onSubmit={form.handleSubmit(submit)}
    >
      <div className='ReprePromote__group'>
        <label className='ReprePromote__groupTitle'>Vyber variantu podpory</label>
        <div className='ReprePromote__typeSelect'>
          <button
            type='button'
            disabled={isLoading}
            onClick={() => {
              form.setValue('type', ReprePromoteType.BASIC, { shouldTouch: true });
              form.setValue('street', null, { shouldTouch: true });
              form.setValue('city', null, { shouldTouch: true });
              form.setValue('zip', null, { shouldTouch: true });
              form.setValue('dressNameTag', null, { shouldTouch: true });
              form.setValue('dressNumber', null, { shouldTouch: true });
              form.setValue('dressSize', null, { shouldTouch: true });
            }}
            className={classNames('ReprePromote__type', {
              'ReprePromote__type--selected': type === ReprePromoteType.BASIC,
            })}
          >
            <strong>Základní podpora</strong>
            <ul>
              <li>Tvoje jméno v srdci týmu</li>
            </ul>
            <div className='ReprePromote__typePrice'>1 499 Kč</div>
          </button>
          <button
            type='button'
            disabled={isLoading}
            onClick={() => form.setValue('type', ReprePromoteType.PREMIUM, { shouldTouch: true })}
            className={classNames('ReprePromote__type', {
              'ReprePromote__type--selected': type === ReprePromoteType.PREMIUM,
            })}
          >
            <strong>Prémiová podpora</strong>
            <ul>
              <li>Reprezentační dres s tvojí jmenovkou</li>
              <li>Oficiální certifikát fanouška</li>
              <li>Tvoje jméno v srdci týmu</li>
            </ul>
            <div className='ReprePromote__typePrice'>3 499 Kč</div>
          </button>
        </div>
      </div>
      <div className='ReprePromote__group'>
        <label className='ReprePromote__groupTitle'>Jak bude dres vypadat?</label>
        <div className='ReprePromote__half'>
          <TextField
            disabled={isLoading}
            label={'Jmenovka do srdce *'}
            error={form.formState.errors?.heartNameTag?.message}
            {...form.register('heartNameTag', {
              required: 'Vyplň prosím jmenovku, kterou chceš do srdce.',
            })}
          />
          {type === ReprePromoteType.PREMIUM && (
            <>
              <TextField
                disabled={isLoading}
                label={'Jmenovka na dres *'}
                error={form.formState.errors?.dressNameTag?.message}
                {...form.register('dressNameTag', {
                  required: 'Vyplň prosím jmenovku, kterou chceš na dres.',
                })}
              />
              <TextField
                disabled={isLoading}
                label={'Číslo na dres *'}
                type='number'
                error={form.formState.errors?.dressNumber?.message}
                {...form.register('dressNumber', {
                  required: 'Vyplň prosím číslo, které chceš na dres.',
                })}
              />
              <SelectField
                disabled={isLoading}
                label={'Velikost dresu *'}
                error={form.formState.errors?.dressSize?.message}
                {...form.register('dressSize', {
                  required: 'Vyplň prosím velikost dresu.',
                  validate: (value, formValues) => {
                    if (value === null && formValues.type === ReprePromoteType.PREMIUM) {
                      return 'Vyplň prosím velikost dresu.';
                    }

                    return true;
                  },
                })}
                value={form.watch('dressSize') ?? '-'}
                onChange={e => {
                  form.setValue(
                    'dressSize',
                    e.target.value === '-' ? null : (e.target.value as DressSize),
                    {
                      shouldTouch: true,
                      shouldValidate: true,
                    },
                  );
                }}
              >
                <option value='-'>Vyber velikost</option>
                <option value='XS'>XS</option>
                <option value='S'>S</option>
                <option value='M'>M</option>
                <option value='L'>L</option>
                <option value='XL'>XL</option>
                <option value='2XL'>2XL</option>
                <option value='3XL'>3XL</option>
                <option value='4XL'>4XL</option>
              </SelectField>
            </>
          )}
        </div>
      </div>
      <div className='ReprePromote__group'>
        <label className='ReprePromote__groupTitle'>Kam máme dres doručit?</label>
        <div className='ReprePromote__half'>
          <TextField
            disabled={isLoading}
            label={'Křestní jméno *'}
            error={form.formState.errors?.firstName?.message}
            {...form.register('firstName', { required: 'Vyplň prosím tvoje křestní jméno.' })}
          />
          <TextField
            disabled={isLoading}
            label={'Příjmení *'}
            error={form.formState.errors?.lastName?.message}
            {...form.register('lastName', { required: 'Vyplň prosím tvoje příjmení.' })}
          />
          <TextField
            disabled={isLoading}
            label={'Ulice a č.p. *'}
            error={form.formState.errors?.street?.message}
            {...form.register('street', { required: 'Vyplň prosím ulici a č.p.' })}
          />
          <TextField
            disabled={isLoading}
            label={'Město *'}
            error={form.formState.errors?.city?.message}
            {...form.register('city', { required: 'Vyplň prosím město.' })}
          />
          <TextField
            disabled={isLoading}
            label={'PSČ *'}
            error={form.formState.errors?.zip?.message}
            {...form.register('zip', {
              required: 'Vyplň prosím PSČ.',
              validate: value => {
                if (!validateZipCode(value)) {
                  return 'Zkontroluj prosím PSČ.';
                }
                return true;
              },
            })}
          />
          <TextField
            disabled={isLoading}
            label={'E-mailová adresa *'}
            type='email'
            error={form.formState.errors?.email?.message}
            {...form.register('email', {
              required: 'Vyplň prosím tvojí e-mailovou adresu.',
              validate: value => {
                if (!validateEmail(value)) {
                  return 'Zkontroluj prosím tvojí e-mailovou adresu.';
                }
                return true;
              },
            })}
          />
          <TextField
            disabled={isLoading}
            label={'Telefonní číslo *'}
            error={form.formState.errors?.phone?.message}
            {...form.register('phone', {
              required: 'Vyplň prosím tvoje telefonní číslo.',
              validate: value => {
                if (!validatePhoneNumber(value)) {
                  return 'Zkontroluj prosím tvoje telefonní číslo.';
                }
                return true;
              },
            })}
          />
        </div>
      </div>
      <div className='ReprePromote__group'>
        <label className='ReprePromote__groupTitle'>Fakturační údaje</label>
        <div className='ReprePromote__half'>
          <TextField
            disabled={isLoading}
            label={'Název subjektu'}
            error={form.formState.errors?.companyName?.message}
            {...form.register('companyName', {})}
          />
          <TextField
            disabled={isLoading}
            label={'Ulice a č.p.'}
            error={form.formState.errors?.billingStreet?.message}
            {...form.register('billingStreet', {})}
          />
          <TextField
            disabled={isLoading}
            label={'Město'}
            error={form.formState.errors?.billingCity?.message}
            {...form.register('billingCity', {})}
          />
          <TextField
            disabled={isLoading}
            label={'PSČ'}
            error={form.formState.errors?.billingZip?.message}
            {...form.register('billingZip', {
              validate: value => {
                if (!value) return true;
                if (!validateZipCode(value)) {
                  return 'Zkontroluj prosím PSČ.';
                }
                return true;
              },
            })}
          />
          <TextField
            disabled={isLoading}
            label={'IČ'}
            error={form.formState.errors?.identificationNumber?.message}
            {...form.register('identificationNumber', {
              validate: value => {
                if (!value) return true;
                if (!validateICO(value)) {
                  return 'Zkontroluj prosím IČ.';
                }
                return true;
              },
            })}
          />
          <TextField
            disabled={isLoading}
            label={'DIČ'}
            error={form.formState.errors?.vatNumber?.message}
            {...form.register('vatNumber', {
              validate: value => {
                if (!value) return true;
                if (!validateDIC(value)) {
                  return 'Zkontroluj prosím DIČ.';
                }
                return true;
              },
            })}
          />
        </div>
      </div>
      <div className='ReprePromote__group'>
        <label className='ReprePromote__groupTitle'>Chceš nám něco sdělit?</label>
        <TextArea
          disabled={isLoading}
          label='Poznámka'
          error={form.formState.errors?.note?.message}
          {...form.register('note')}
        />
      </div>
      <div>
        <Button type='submit' variant='blue-outline' disabled={isLoading}>
          {!isLoading ? 'Podpořit reprezentaci' : 'Odesílání...'}
        </Button>
      </div>
    </form>
  );
}

export default ReprePromote;
